"use client";
import { Bar, BarChart, XAxis, YAxis, Cell } from "recharts";
import { useState } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

import useTopContractors from "./useTopContractors";
import useSpendingByRecipientType from "./useSpendingByRecipientType.js";
import useSpendingBySetAside from "./useSpendingBySetAside.js";

// Normalize agency name for consistent API calls and display
function normalizeAgencyName(rawName) {
  if (!rawName) return null;

  const lowercaseWords = new Set([
    "of",
    "and",
    "the",
    "for",
    "in",
    "on",
    "at",
    "by",
    "to",
  ]);

  return rawName
    .trim()
    .toLowerCase()
    .replace(/^dept\b|^department\b/i, "department")
    .split(" ")
    .map((word, index) => {
      if (index > 0 && lowercaseWords.has(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

// Format agency name for display in titles
function formatAgencyNameForDisplay(rawName) {
  if (!rawName) return "";

  // First normalize it
  const normalized = normalizeAgencyName(rawName);
  if (!normalized) return "";

  // Then format for display - capitalize first letter of each word except articles/prepositions
  return normalized
    .split(" ")
    .map((word, index) => {
      // Always capitalize first word
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      // Keep lowercase words as is
      const lowercaseWords = new Set([
        "of",
        "and",
        "the",
        "for",
        "in",
        "on",
        "at",
        "by",
        "to",
      ]);
      if (lowercaseWords.has(word)) {
        return word;
      }
      // Capitalize other words
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

// Format dollar amount in a more readable way
function formatCurrency(amount) {
  if (amount === undefined || amount === null) return "$0";

  // For billions (1,000,000,000+)
  if (amount >= 1000000000) {
    return `$${(amount / 1000000000).toFixed(1)}B`;
  }
  // For millions (1,000,000+)
  else if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  // For thousands (1,000+)
  else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  // For smaller amounts
  else {
    return `$${amount.toLocaleString()}`;
  }
}

// Format contractor name with Title Case (capitalize first letter of each word)
function formatContractorName(name) {
  if (!name) return "";

  return name
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" "); // Keep spaces between words
}

function ContractorCard({ contractor }) {
  // Handle potential missing data
  if (!contractor || !contractor["Recipient Name"]) {
    return null;
  }

  const camelCaseName = formatContractorName(contractor["Recipient Name"]);

  return (
    <Card className="w-full shadow-sm h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base transition-colors hover:text-primary">
          <a
            href={`https://www.usaspending.gov/recipient/${
              contractor["recipient_id"] || ""
            }`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {camelCaseName}
          </a>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm">
          <CardDescription>
            <strong>Awarded Amount:</strong>{" "}
            {formatCurrency(contractor["Award Amount"])}
          </CardDescription>

          {contractor["Awarding Agency"] && (
            <CardDescription>
              <strong>Agency:</strong>
              {contractor["Awarding Sub Agency"] &&
                ` ${contractor["Awarding Sub Agency"]}`}
            </CardDescription>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function TopContractors({ naics, agencyName, naicsDescription }) {
  // Normalize agency name once
  const naicsString = String(naics);
  const normalizedAgencyName = agencyName
    ? normalizeAgencyName(agencyName)
    : null;
  const displayAgencyName = agencyName
    ? formatAgencyNameForDisplay(agencyName)
    : "";

  // State for expandable contractors list
  const [visibleContractors, setVisibleContractors] = useState(6);

  const {
    data: contractorData,
    loading: contractorsLoading,
    error: contractorsError,
  } = useTopContractors(naicsString, normalizedAgencyName);

  const {
    data: setAsideData,
    loading: setAsideLoading,
    error: setAsideError,
  } = useSpendingByRecipientType(naicsString, normalizedAgencyName);

  const {
    groupedData: setAsideTypeData,
    loading: setAsideTypeLoading,
    error: setAsideTypeError,
  } = useSpendingBySetAside(naicsString, normalizedAgencyName);

  // Prepare chart data with formatted names
  const chartData =
    setAsideData?.map((item) => ({
      name: item.formatted_name,
      amount: item.obligation,
      originalName: item.set_aside,
    })) || [];

  // Check if we have any data to display
  const hasContractorData = contractorData && contractorData.length > 0;
  const hasSetAsideData =
    chartData && chartData.some((item) => item.amount > 0);
  const hasSetAsideTypeData = setAsideTypeData && setAsideTypeData.length > 0;

  // Create chart configs for shadcn/ui charts
  const businessTypeChartConfig = {
    amount: {
      label: "Amount",
    },
    ...chartData.reduce((config, item, index) => {
      config[item.name] = {
        label: item.name,
        color: `hsl(var(--chart-${(index % 5) + 1}))`,
      };
      return config;
    }, {}),
  };

  const setAsideChartConfig = {
    amount: {
      label: "Amount",
    },
    ...(setAsideTypeData?.reduce((config, item, index) => {
      config[item.name] = {
        label: item.name,
        color: `hsl(var(--chart-${(index % 5) + 1}))`,
      };
      return config;
    }, {}) || {}),
  };

  const debugMode = process.env.NODE_ENV === "development";

  return (
    <div className="space-y-8 mt-4 pb-8">
      {!naics ? (
        <div className="text-center p-6 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-amber-800">
            No NAICS code provided. Contract data cannot be displayed.
          </p>
        </div>
      ) : (
        <>
          {/* Top Contractors Grid */}
          <Card className="w-full shadow-sm mb-8">
            <CardHeader>
              <CardTitle>
                Top Small Business Contractors
                {displayAgencyName ? ` ${displayAgencyName}` : ""}
              </CardTitle>
              <CardDescription>
                NAICS code: {naics}
                {naicsDescription ? ` ${naicsDescription}` : ""}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {contractorsLoading ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-32 w-full" />
                  ))}
                </div>
              ) : contractorsError ? (
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-700">
                    No contractors found for the selected criteria.
                  </p>
                </div>
              ) : !hasContractorData ? (
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-700">
                    No contractors found for the selected criteria.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                    {contractorData
                      .slice(0, visibleContractors)
                      .map((contractor) => (
                        <ContractorCard
                          key={
                            contractor["Award ID"] || Math.random().toString()
                          }
                          contractor={contractor}
                        />
                      ))}
                  </div>
                  {contractorData.length > visibleContractors && (
                    <div className="flex justify-center mt-6">
                      <Button
                        variant="outline"
                        onClick={() =>
                          setVisibleContractors((prev) => prev + 6)
                        }
                        className="text-sm"
                      >
                        Show More Contractors
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
          {/* Spending by Set-Aside Type Bar Chart - Using shadcn/ui chart */}
          <Card className="w-full shadow-sm">
            <CardHeader>
              <CardTitle>
                {displayAgencyName ? ` ${displayAgencyName} ` : ""}
                Set-Aside Distribution{" "}
                {naicsDescription ? `${naicsDescription}` : ""}
              </CardTitle>
              <CardDescription>Contract set-aside categories</CardDescription>
            </CardHeader>
            <CardContent>
              {setAsideTypeLoading ? (
                <Skeleton className="h-96 w-full" />
              ) : setAsideTypeError ? (
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-700">
                    No set-aside category data available for the selected
                    criteria.
                  </p>
                </div>
              ) : !hasSetAsideTypeData ? (
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-700">
                    No set-aside category data available for the selected
                    criteria.
                  </p>
                </div>
              ) : (
                <ChartContainer
                  config={setAsideChartConfig}
                  className="min-h-[400px]"
                >
                  <BarChart
                    accessibilityLayer
                    data={setAsideTypeData}
                    layout="vertical"
                    margin={{
                      left: 20,
                      right: 30,
                      top: 20,
                      bottom: 70,
                    }}
                  >
                    <YAxis
                      dataKey="name"
                      type="category"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      width={150}
                    />
                    <XAxis
                      dataKey="amount"
                      type="number"
                      tickFormatter={(val) => formatCurrency(val)}
                    />
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent
                          formatter={(val) => formatCurrency(Number(val))}
                        />
                      }
                    />
                    <Bar dataKey="amount" layout="vertical" radius={5}>
                      {setAsideTypeData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={`hsl(var(--chart-${(index % 5) + 1}))`}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
            <CardFooter className="flex-col items-start gap-2 text-sm">
              <div className="leading-none text-muted-foreground">
                Showing distribution of contracts by set-aside category
              </div>
            </CardFooter>
          </Card>

          {/* Spending by Set-Aside Bar Chart - Using shadcn/ui chart */}
          <Card className="w-full shadow-sm mb-8">
            <CardHeader>
              <CardTitle>
                {displayAgencyName ? `${displayAgencyName}` : ""} Spending{" "}
                {naicsDescription ? `${naicsDescription}` : ""}
              </CardTitle>
              <CardDescription>Business type distribution</CardDescription>
            </CardHeader>
            <CardContent>
              {setAsideLoading ? (
                <Skeleton className="h-96 w-full" />
              ) : setAsideError ? (
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-700">
                    No contractors found for the selected criteria.
                  </p>
                </div>
              ) : !hasSetAsideData ? (
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-700">
                    No business type data available for the selected criteria.
                  </p>
                </div>
              ) : (
                <ChartContainer
                  config={businessTypeChartConfig}
                  className="min-h-[400px]"
                >
                  <BarChart
                    accessibilityLayer
                    data={chartData}
                    layout="vertical"
                    margin={{
                      left: 20,
                      right: 30,
                      top: 20,
                      bottom: 70,
                    }}
                  >
                    <YAxis
                      dataKey="name"
                      type="category"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      width={150}
                    />
                    <XAxis
                      dataKey="amount"
                      type="number"
                      tickFormatter={(val) => formatCurrency(val)}
                    />
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent
                          formatter={(val) => formatCurrency(Number(val))}
                        />
                      }
                    />
                    <Bar dataKey="amount" layout="vertical" radius={5}>
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={`hsl(var(--chart-${(index % 5) + 1}))`}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
            <CardFooter className="flex-col items-start gap-2 text-sm">
              <div className="leading-none text-muted-foreground">
                Showing spending distribution by business type
              </div>
            </CardFooter>
          </Card>
        </>
      )}
      {debugMode && contractorsError && (
        <Card className="w-full mt-8 bg-gray-50">
          <CardHeader>
            <CardTitle className="text-red-600">Debug Information</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs overflow-auto p-4 bg-gray-100 rounded">
              {JSON.stringify(contractorsError, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
